import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Form, Button, Row, Col, Badge, OverlayTrigger, Popover, Modal } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { LICHHOCLIENTUC_API, LOPDAOTAOLIENTUC_TRIENKHAI_API, DONVI_API, ACCOUNT_AUTHINFO_API } from '../../../../utils/apiUrl';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import { getUser } from '../../../../utils/oidc';
import CreateScheduleLienTucModal from './CreateScheduleLienTucModal';
import EditScheduleLienTucModal from './EditScheduleLienTucModal';

// Helper gom nhóm các buổi học ngắt quãng thành một giai đoạn đào tạo duy nhất
const groupSchedules = (list) => {
    if (!list || list.length === 0) return [];
    const map = new Map();

    list.forEach(item => {
        let groupKey;
        const bId = item.batchId || item.nganhDaoTaoId;
        const hv = item.hoSoTuyenSinhId || item.hocVienTen || '';
        if (bId && bId !== '00000000-0000-0000-0000-000000000000') {
            groupKey = `batch_${bId}_${hv}`;
        } else {
            // Fallback gom nhóm cho dữ liệu cũ chưa có batchId
            const donVi = (item.donVi || '').trim();
            const gv = item.giangVienId || item.giangVienTen || (item.giangViens && item.giangViens[0]?.giangVienTen) || '';
            const td = (item.trinhDo || '').trim();
            const gc = (item.ghiChu || '').trim();
            groupKey = `comp_${hv}_${donVi}_${gv}_${td}_${gc}`;
        }

        if (!map.has(groupKey)) {
            map.set(groupKey, {
                key: groupKey,
                items: [],
                representative: item,
                hoSoTuyenSinhId: item.hoSoTuyenSinhId,
                hocVienTen: item.hocVienTen,
                donVi: item.donVi,
                giangVienTen: item.giangVienTen,
                giangViens: item.giangViens,
                trinhDo: item.trinhDo,
                ghiChu: item.ghiChu,
                trangThai: item.trangThai,
                batchId: bId || null,
            });
        }
        map.get(groupKey).items.push(item);
    });

    return Array.from(map.values()).map(group => {
        group.items.sort((a, b) => new Date(a.ngayBatDau) - new Date(b.ngayBatDau));
        const dates = group.items.map(it => moment(it.ngayBatDau).format('DD/MM/YYYY')).filter(Boolean);
        const isSingleRange = group.items.length === 1 && group.items[0].ngayKetThuc && group.items[0].ngayKetThuc !== group.items[0].ngayBatDau;
        return {
            ...group,
            dates,
            isSingleRange,
            minDate: dates[0] || '',
            maxDate: dates[dates.length - 1] || ''
        };
    });
};

const ScheduleLienTucTab = ({ lopId, lopDetail }) => {
    const queryClient = useQueryClient();

    // 0. Fetch Current Logged-in User
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        getUser().then(u => {
            if (u?.profile) setCurrentUser(u.profile);
        });
    }, []);

    const currentUserId = currentUser?.sub || currentUser?.id || '';

    // View state: 'matrix' (default) | 'list' | 'student' | 'instructor'
    const [viewMode, setViewMode] = useState('matrix');

    // Matrix target for Admin: 'student' (Ma trận học viên) | 'instructor' (Ma trận giảng viên)
    const [matrixTarget, setMatrixTarget] = useState('student');

    // Mobile Sub Mode: 'student' (Lịch học viên) | 'instructor' (Lịch giảng viên)
    const [mobileSubMode, setMobileSubMode] = useState('student');
    // Mobile View Type: 'by_day' (Theo ngày) | 'by_roadmap' (Theo học viên / Theo giảng viên)
    const [mobileViewType, setMobileViewType] = useState('by_day');
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailItem, setDetailItem] = useState(null);

    const handleOpenDetail = (item) => {
        setDetailItem(item);
        setShowDetailModal(true);
    };

    // Quick create state from matrix cell click
    const [quickCreateState, setQuickCreateState] = useState({
        studentId: null,
        date: null
    });

    // Matrix view month/year
    const [thang, setThang] = useState(moment().month() + 1);
    const [nam, setNam] = useState(moment().year());

    // Helper format thứ tiếng Việt
    const formatVnDay = (m) => {
        const map = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        const fullMap = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];
        const d = m.day();
        return { short: map[d], full: fullMap[d] };
    };

    // Helper lấy chữ viết tắt avatar
    const getInitials = (name) => {
        if (!name) return 'HV';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // 7 ngày quanh selectedDate cho Dải ngày (Day Strip)
    const dayStripDates = useMemo(() => {
        const center = moment(selectedDate, 'YYYY-MM-DD');
        return [-3, -2, -1, 0, 1, 2, 3].map(offset => moment(center).add(offset, 'days'));
    }, [selectedDate]);

    // Search and filters
    const [studentFilter, setStudentFilter] = useState('');
    const [tutorFilter, setTutorFilter] = useState('');
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [selectedInstructorId, setSelectedInstructorId] = useState('');

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // 1. Fetch Students in Class
    const { data: studentsData = [] } = useQuery(
        ['studentsLienTuc', lopId],
        () => Get(`${LOPDAOTAOLIENTUC_TRIENKHAI_API.GET_HOCVIENS}/${lopId}`),
        {
            enabled: !!lopId,
            select: (res) => res?.$values || res || []
        }
    );

    // 2. Fetch Tutors in Class with SSO Profile
    const { data: tutorsData = [] } = useQuery(
        ['giangViensLienTuc', lopId],
        async () => {
            const res = await Get(`${LOPDAOTAOLIENTUC_TRIENKHAI_API.GET_GIANGVIENS}/${lopId}`);
            const accountIds = res?.$values || res || [];

            const detailTasks = accountIds.map(async (accountId) => {
                try {
                    const accountInfo = await Get(ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT, accountId);
                    const profile = accountInfo?.accountAuthInfo || accountInfo?.AccountAuthInfo || accountInfo;
                    const lastName = profile?.lastName || profile?.LastName || "";
                    const middleName = profile?.middleName || profile?.MiddleName || "";
                    const firstName = profile?.firstName || profile?.FirstName || "";
                    const fullName = [lastName, middleName, firstName].map(n => n?.trim()).filter(Boolean).join(" ");

                    return {
                        id: accountId,
                        accountId: accountId,
                        fullName: fullName || "Chưa xác định",
                        identityCard: profile?.identityCardNumber || profile?.IdentityCardNumber || profile?.identityCard || profile?.IdentityCard || ""
                    };
                } catch {
                    return { id: accountId, accountId, fullName: "Giảng viên" };
                }
            });

            return await Promise.all(detailTasks);
        },
        { enabled: !!lopId }
    );

    // Xác định vai trò người dùng hiện tại trong lớp
    const currentStudent = useMemo(() => {
        if (!currentUserId || studentsData.length === 0) return null;
        return studentsData.find(s => s.accountId === currentUserId || s.id === currentUserId) || null;
    }, [studentsData, currentUserId]);

    const currentTutor = useMemo(() => {
        if (!currentUserId || tutorsData.length === 0) return null;
        return tutorsData.find(t => t.accountId === currentUserId || t.id === currentUserId) || null;
    }, [tutorsData, currentUserId]);

    const isStudent = !!currentStudent;
    const isInstructor = !!currentTutor && !isStudent;
    const isAdmin = !isStudent && !isInstructor;

    const studentOptions = useMemo(() => {
        return studentsData.map(s => ({
            value: s.id,
            label: `${s.hoTen || 'Học viên'}${s.soCccd ? ` (${s.soCccd})` : ''}`
        }));
    }, [studentsData]);

    const instructorOptions = useMemo(() => {
        return tutorsData.map(t => ({
            value: t.id || t.accountId,
            label: `${t.fullName || 'Giảng viên'}${t.identityCard ? ` (${t.identityCard})` : ''}`
        }));
    }, [tutorsData]);

    // Tự động gán ID mặc định và viewMode theo vai trò
    useEffect(() => {
        if (isStudent && currentStudent) {
            setViewMode('matrix');
            setSelectedStudentId(currentStudent.id);
        } else if (isInstructor && currentTutor) {
            setViewMode('matrix');
            setSelectedInstructorId(currentTutor.id || currentTutor.accountId);
        } else if (isAdmin) {
            if (studentsData.length > 0 && !selectedStudentId) {
                setSelectedStudentId(studentsData[0].id);
            }
            if (tutorsData.length > 0 && !selectedInstructorId) {
                setSelectedInstructorId(tutorsData[0].id || tutorsData[0].accountId);
            }
        }
    }, [isStudent, currentStudent, isInstructor, currentTutor, isAdmin, studentsData, tutorsData]);

    // 3. Fetch Departments (Khoa / Đơn vị)
    const { data: donViList = [] } = useQuery(
        'donViListLienTuc',
        () => Get(DONVI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }),
        {
            select: (res) => res?.data?.data || res?.data || res || []
        }
    );

    // 4. Fetch Detailed Schedule List (Dành cho Quản trị viên xem toàn lớp)
    const {
        data: scheduleList = [],
        isLoading: isScheduleLoading,
        refetch: refetchSchedule
    } = useQuery(
        ['scheduleListLienTuc', lopId],
        () => Get(`${LICHHOCLIENTUC_API.GET_BY_COURSE}/${lopId}`),
        {
            enabled: !!lopId && (viewMode === 'list' || viewMode === 'matrix'),
            select: (res) => res?.$values || res?.data?.$values || res?.data || res || []
        }
    );

    // 4.1. Fetch Lịch Giảng Viên Cá Nhân (Sử dụng API GetLichGiangVienCaNhan)
    const {
        data: instructorScheduleList = [],
        isLoading: isInstructorLoading,
        refetch: refetchInstructorSchedule
    } = useQuery(
        ['instructorScheduleLienTuc', lopId, selectedInstructorId],
        () => Get(`${LICHHOCLIENTUC_API.GET_LICH_GIANG_VIEN_CA_NHAN}/${lopId}${selectedInstructorId ? `?giangVienId=${selectedInstructorId}` : ''}`),
        {
            enabled: !!lopId && (viewMode === 'instructor' || mobileSubMode === 'instructor'),
            select: (res) => res?.$values || res?.data?.$values || res?.data || res || []
        }
    );

    // 4.2. Fetch Lịch Học Viên Cá Nhân (Sử dụng API GetLichHocVienCaNhan)
    const {
        data: studentScheduleList = [],
        isLoading: isStudentLoading,
        refetch: refetchStudentSchedule
    } = useQuery(
        ['studentScheduleLienTuc', lopId, selectedStudentId],
        () => Get(`${LICHHOCLIENTUC_API.GET_LICH_HOC_VIEN_CA_NHAN}/${lopId}${selectedStudentId ? `?hocVienId=${selectedStudentId}` : ''}`),
        {
            enabled: !!lopId && (viewMode === 'student' || mobileSubMode === 'student'),
            select: (res) => res?.$values || res?.data?.$values || res?.data || res || []
        }
    );

    // 5. Fetch Matrix Schedule cho Quản trị viên theo Học Viên (GET_ADMIN_MATRIX)
    const {
        data: adminMatrixData,
        isLoading: isAdminMatrixLoading,
        refetch: refetchAdminMatrix
    } = useQuery(
        ['adminMatrixLienTuc', lopId, thang, nam],
        () => Get(`${LICHHOCLIENTUC_API.GET_ADMIN_MATRIX}/${lopId}?thang=${thang}&nam=${nam}`),
        {
            enabled: !!lopId && ((viewMode === 'matrix' && (!isAdmin || matrixTarget === 'student')) || (mobileViewType === 'by_day' && mobileSubMode === 'student')),
            select: (res) => res?.data || res
        }
    );

    // 5.0. Fetch Matrix Schedule cho Quản trị viên theo Giảng Viên (GET_ADMIN_INSTRUCTOR_MATRIX)
    const {
        data: adminInstructorMatrixData,
        isLoading: isAdminInstructorMatrixLoading,
        refetch: refetchAdminInstructorMatrix
    } = useQuery(
        ['adminInstructorMatrixLienTuc', lopId, thang, nam],
        () => Get(`${LICHHOCLIENTUC_API.GET_ADMIN_INSTRUCTOR_MATRIX}/${lopId}?thang=${thang}&nam=${nam}`),
        {
            enabled: !!lopId && ((viewMode === 'matrix' && isAdmin && matrixTarget === 'instructor') || (mobileViewType === 'by_day' && mobileSubMode === 'instructor')),
            select: (res) => res?.data || res
        }
    );

    // 5.1. Fetch Matrix Schedule cho Học Viên Cá Nhân (GET_MATRIX_HOC_VIEN_CA_NHAN)
    const {
        data: studentMatrixData,
        isLoading: isStudentMatrixLoading,
        refetch: refetchStudentMatrix
    } = useQuery(
        ['studentMatrixLienTuc', lopId, thang, nam, selectedStudentId],
        () => Get(`${LICHHOCLIENTUC_API.GET_MATRIX_HOC_VIEN_CA_NHAN}/${lopId}?thang=${thang}&nam=${nam}${selectedStudentId ? `&hocVienId=${selectedStudentId}` : ''}`),
        {
            enabled: !!lopId && (viewMode === 'student' || (viewMode === 'matrix' && isStudent)),
            select: (res) => res?.data || res
        }
    );

    // 5.2. Fetch Matrix Schedule cho Giảng Viên Cá Nhân (GET_MATRIX_GIANG_VIEN_CA_NHAN)
    const {
        data: instructorMatrixData,
        isLoading: isInstructorMatrixLoading,
        refetch: refetchInstructorMatrix
    } = useQuery(
        ['instructorMatrixLienTuc', lopId, thang, nam, selectedInstructorId],
        () => Get(`${LICHHOCLIENTUC_API.GET_MATRIX_GIANG_VIEN_CA_NHAN}/${lopId}?thang=${thang}&nam=${nam}${selectedInstructorId ? `&giangVienId=${selectedInstructorId}` : ''}`),
        {
            enabled: !!lopId && (viewMode === 'instructor' || (viewMode === 'matrix' && isInstructor)),
            select: (res) => res?.data || res
        }
    );

    const matrixData = isStudent 
        ? studentMatrixData 
        : isInstructor 
            ? instructorMatrixData 
            : (matrixTarget === 'instructor' ? adminInstructorMatrixData : adminMatrixData);

    const isMatrixLoading = isStudent 
        ? isStudentMatrixLoading 
        : isInstructor 
            ? isInstructorMatrixLoading 
            : (matrixTarget === 'instructor' ? isAdminInstructorMatrixLoading : isAdminMatrixLoading);

    // Handle Delete Single Row
    const handleConfirmDelete = async () => {
        if (!itemToDelete?.id) return;
        try {
            await DeleteSingle(LICHHOCLIENTUC_API.DELETE_ITEM, itemToDelete.id);
            toast.success('Đã xóa dòng lịch học thành công.');
            refetchSchedule();
            refetchInstructorSchedule();
            refetchStudentSchedule();
            refetchAdminMatrix();
            refetchAdminInstructorMatrix();
            refetchStudentMatrix();
            refetchInstructorMatrix();
        } catch (err) {
            toast.error(err?.message || 'Không thể xóa dòng lịch học.');
        } finally {
            setShowDeleteModal(false);
            setItemToDelete(null);
        }
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setShowEditModal(true);
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const handleRefetchAll = () => {
        refetchSchedule();
        refetchInstructorSchedule();
        refetchStudentSchedule();
        refetchAdminMatrix();
        refetchAdminInstructorMatrix();
        refetchStudentMatrix();
        refetchInstructorMatrix();
    };

    // Filtered Schedule List
    const filteredScheduleList = scheduleList.filter(item => {
        const matchStudent = !studentFilter || (item.hocVienTen && item.hocVienTen.toLowerCase().includes(studentFilter.toLowerCase().trim()));
        const gvTen = item.giangVienTen || (item.giangViens && item.giangViens.map(g => g.giangVienTen).join(' ')) || '';
        const matchTutor = !tutorFilter || gvTen.toLowerCase().includes(tutorFilter.toLowerCase().trim());
        return matchStudent && matchTutor;
    });

    // Matrix dates & rows (được trả về chuẩn từ Backend)
    const matrixDates = matrixData?.dates?.$values || matrixData?.dates || [];
    const matrixRows = (matrixData?.rows?.$values || matrixData?.rows || []).filter(r => {
        const name = r.hocVienTen || '';
        if (isAdmin && matrixTarget === 'instructor') {
            return !tutorFilter || name.toLowerCase().includes(tutorFilter.toLowerCase().trim());
        }
        return !studentFilter || name.toLowerCase().includes(studentFilter.toLowerCase().trim());
    });

    // Dữ liệu gom nhóm đồng bộ cho các views
    const groupedScheduleList = useMemo(() => groupSchedules(scheduleList), [scheduleList]);
    const groupedFilteredScheduleList = useMemo(() => groupSchedules(filteredScheduleList), [filteredScheduleList]);

    // Student Timeline data (Sử dụng dữ liệu từ API GetLichHocVienCaNhan)
    const studentSchedules = useMemo(() => {
        if (!thang || !nam) return studentScheduleList;
        return studentScheduleList.filter(item => {
            const start = moment(item.ngayBatDau);
            const end = item.ngayKetThuc ? moment(item.ngayKetThuc) : start;
            const targetMonthStart = moment(`${nam}-${String(thang).padStart(2, '0')}-01`, 'YYYY-MM-DD').startOf('month');
            const targetMonthEnd = moment(targetMonthStart).endOf('month');
            return start.isSameOrBefore(targetMonthEnd) && end.isSameOrAfter(targetMonthStart);
        });
    }, [studentScheduleList, thang, nam]);
    const groupedStudentSchedules = useMemo(() => groupSchedules(studentSchedules), [studentSchedules]);

    // Instructor Agenda data (Sử dụng dữ liệu từ API GetLichGiangVienCaNhan)
    const instructorSchedules = useMemo(() => {
        if (!thang || !nam) return instructorScheduleList;
        return instructorScheduleList.filter(item => {
            const start = moment(item.ngayBatDau);
            const end = item.ngayKetThuc ? moment(item.ngayKetThuc) : start;
            const targetMonthStart = moment(`${nam}-${String(thang).padStart(2, '0')}-01`, 'YYYY-MM-DD').startOf('month');
            const targetMonthEnd = moment(targetMonthStart).endOf('month');
            return start.isSameOrBefore(targetMonthEnd) && end.isSameOrAfter(targetMonthStart);
        });
    }, [instructorScheduleList, thang, nam]);
    const groupedInstructorSchedules = useMemo(() => groupSchedules(instructorSchedules), [instructorSchedules]);

    // 4.3. KPI & Danh sách học viên theo ngày cho Mobile Matrix
    const studentDayKpis = useMemo(() => {
        const rows = adminMatrixData?.rows?.$values || adminMatrixData?.rows || [];
        let coLich = 0;
        let chuaXep = 0;
        let vangNghi = 0;
        const items = [];

        rows.forEach(r => {
            const cell = r.scheduleByDate ? r.scheduleByDate[selectedDate] : null;
            const trangThai = cell?.trangThai || '-';
            if (trangThai === 'Hoc') coLich++;
            else if (trangThai === 'Nghi') vangNghi++;
            else chuaXep++;

            items.push({
                studentId: r.hoSoTuyenSinhId,
                studentName: r.hocVienTen,
                cell: cell,
                trangThai: trangThai
            });
        });

        return { coLich, chuaXep, vangNghi, items };
    }, [adminMatrixData, selectedDate]);

    // 4.4. KPI & Danh sách giảng viên theo ngày cho Mobile Matrix
    const instructorDayKpis = useMemo(() => {
        const rows = adminInstructorMatrixData?.rows?.$values || adminInstructorMatrixData?.rows || [];
        let coLich = 0;
        let conTrong = 0;
        const items = [];

        rows.forEach(r => {
            const cell = r.scheduleByDate ? r.scheduleByDate[selectedDate] : null;
            const trangThai = cell?.trangThai || '-';
            if (trangThai === 'Hoc') coLich++;
            else conTrong++;

            items.push({
                tutorId: r.hoSoTuyenSinhId,
                tutorName: r.hocVienTen,
                cell: cell,
                trangThai: trangThai
            });
        });

        // Check conflict (giảng viên có >= 2 ca/buổi ở các khoa khác nhau cùng ngày)
        const dayLichs = scheduleList.filter(l => {
            const start = moment(l.ngayBatDau).startOf('day');
            const end = l.ngayKetThuc ? moment(l.ngayKetThuc).endOf('day') : start;
            const cur = moment(selectedDate).startOf('day');
            return cur.isSameOrAfter(start) && cur.isSameOrBefore(end);
        });

        const tutorMap = new Map();
        dayLichs.forEach(l => {
            const gvs = l.giangViens || [];
            gvs.forEach(g => {
                const gvId = g.giangVienLienTucId || g.giangVienTen;
                if (!tutorMap.has(gvId)) tutorMap.set(gvId, []);
                tutorMap.get(gvId).push(l);
            });
        });

        const conflictTutors = [];
        tutorMap.forEach((lichList, gvId) => {
            const uniqueDonVis = new Set(lichList.map(x => x.donVi || x.donViId));
            if (uniqueDonVis.size > 1) {
                conflictTutors.push(gvId);
            }
        });

        return {
            coLich,
            conTrong,
            xungDot: conflictTutors.length,
            conflictTutors,
            items
        };
    }, [adminInstructorMatrixData, scheduleList, selectedDate]);

    // 4.5. Tải giảng viên trong tháng (Workload Progress Bar)
    const tutorWorkloads = useMemo(() => {
        const rows = adminInstructorMatrixData?.rows?.$values || adminInstructorMatrixData?.rows || [];
        const dates = adminInstructorMatrixData?.dates?.$values || adminInstructorMatrixData?.dates || [];
        const workDaysInMonth = dates.filter(d => moment(d).day() !== 0).length || 22;

        return rows.map(r => {
            let activeDays = 0;
            if (r.scheduleByDate) {
                dates.forEach(d => {
                    if (r.scheduleByDate[d]?.trangThai === 'Hoc') activeDays++;
                });
            }
            const pct = Math.min(100, Math.round((activeDays / workDaysInMonth) * 100));
            return {
                tutorId: r.hoSoTuyenSinhId,
                tutorName: r.hocVienTen,
                activeDays,
                totalDays: workDaysInMonth,
                pct
            };
        }).sort((a, b) => b.activeDays - a.activeDays);
    }, [adminInstructorMatrixData]);

    return (
        <div>
            {/* ===================== DESKTOP VIEW (>= 768px) ===================== */}
            <div className="d-none d-md-block">
                {/* TOOLBAR: VIEW SWITCHER + ACTION BUTTONS */}
                <div className="d-flex flex-column flex-xl-row justify-content-between align-items-stretch align-items-xl-center mb-3 p-2 bg-white rounded-lg border shadow-sm" style={{ gap: '10px' }}>
                <div className="btn-group flex-nowrap overflow-auto" role="group">
                    <Button
                        variant={viewMode === 'matrix' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setViewMode('matrix')}
                        style={{ 
                            borderTopLeftRadius: '6px', 
                            borderBottomLeftRadius: '6px', 
                            whiteSpace: 'nowrap', padding: '7px 14px', fontWeight: '500' 
                        }}
                    >
                        <i className="fas fa-table mr-1"></i> {isAdmin ? "Ma Trận Tổng Thể" : "Ma Trận Tháng"}
                    </Button>
                    {(isAdmin || isStudent) && (
                        <Button
                            variant={viewMode === 'student' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => setViewMode('student')}
                            style={{ 
                                borderTopRightRadius: (isAdmin && !isInstructor) || isStudent ? '6px' : 0,
                                borderBottomRightRadius: (isAdmin && !isInstructor) || isStudent ? '6px' : 0,
                                borderRadius: isAdmin ? 0 : undefined,
                                whiteSpace: 'nowrap', padding: '7px 14px', fontWeight: '500' 
                            }}
                        >
                            <i className="fas fa-graduation-cap mr-1"></i> {isStudent ? "Lịch Học Chi Tiết Của Tôi" : "Lộ Trình Theo Học Viên"}
                        </Button>
                    )}
                    {(isAdmin || isInstructor) && (
                        <Button
                            variant={viewMode === 'instructor' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => setViewMode('instructor')}
                            style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', whiteSpace: 'nowrap', padding: '7px 14px', fontWeight: '500' }}
                        >
                            <i className="fas fa-user-tie mr-1"></i> {isInstructor ? "Lịch Giảng Dạy Của Tôi" : "Lịch Theo Giảng Viên"}
                        </Button>
                    )}
                </div>

                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <div className="d-none d-lg-flex align-items-center mr-1">
                        <span className="badge badge-light border text-primary px-3 py-2" style={{ fontSize: '13px' }}>
                            <i className="fas fa-hospital-alt mr-1 text-primary"></i>
                            Khoa: <strong>{lopDetail?.khoaDeXuatTen || "Khoa chuyên môn"}</strong>
                        </span>
                    </div>
                    {isAdmin && (
                        <Button
                            variant="primary"
                            size="sm"
                            className="shadow-sm font-weight-bold"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <i className="fas fa-plus-circle mr-1"></i> Phân Lịch Học
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefetchAll}
                        title="Tải lại dữ liệu"
                    >
                        <i className="fas fa-sync-alt mr-1"></i> Tải lại
                    </Button>
                </div>
            </div>

            {/* VIEW 1: DETAILED LIST VIEW */}
            {viewMode === 'list' && (
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-0">
                        <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 360px)' }}>
                            <Table bordered hover size="sm" className="mb-0 align-middle">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={{ width: 50 }} className="text-center">#</th>
                                        <th style={{ minWidth: 150 }}>Học viên</th>
                                        <th style={{ minWidth: 220 }}>Khoa / Đơn vị thực hành</th>
                                        <th style={{ minWidth: 200 }}>Thời gian đào tạo</th>
                                        <th style={{ minWidth: 160 }}>Giảng viên phụ trách</th>
                                        <th style={{ minWidth: 130 }}>Trình độ / Giai đoạn</th>
                                        <th style={{ minWidth: 120 }}>Ghi chú</th>
                                        <th className="text-center" style={{ width: 110 }}>Trạng thái</th>
                                        {isAdmin && <th className="text-center" style={{ width: 90 }}>Thao tác</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {isScheduleLoading ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 9 : 8} className="text-center py-4">
                                                <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                                                Đang tải danh sách phân lịch...
                                            </td>
                                        </tr>
                                    ) : groupedFilteredScheduleList.length === 0 ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 9 : 8} className="text-center py-5 text-muted">
                                                <i className="far fa-calendar-times fa-3x mb-3 text-secondary d-block" style={{ opacity: 0.4 }}></i>
                                                Chưa có lịch học nào được phân công.
                                                {isAdmin && (
                                                    <div className="mt-2">
                                                        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                                                            <i className="fas fa-plus mr-1"></i> Phân lịch học ngay
                                                        </Button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        groupedFilteredScheduleList.map((item, idx) => {
                                            const gvName = item.giangVienTen || (item.giangViens && item.giangViens[0]?.giangVienTen) || '-';

                                            return (
                                                <tr key={item.key || item.id || idx}>
                                                    <td className="text-center text-muted">{idx + 1}</td>
                                                    <td className="font-weight-bold text-dark">
                                                        <i className="fas fa-user-graduate text-muted mr-2"></i>
                                                        {item.hocVienTen || 'Chưa rõ tên'}
                                                    </td>
                                                    <td style={{ minWidth: 220, whiteSpace: 'normal' }}>
                                                        <span className="font-weight-500 text-primary">
                                                            <i className="fas fa-hospital-alt mr-1"></i>
                                                            {item.donVi || 'Chưa chọn khoa'}
                                                        </span>
                                                    </td>
                                                    <td style={{ minWidth: 200, whiteSpace: 'normal' }}>
                                                        {item.items.length === 1 ? (
                                                            item.isSingleRange ? (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark" style={{ fontSize: '12px' }}>
                                                                    <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                    {moment(item.items[0].ngayBatDau).format('DD/MM/YYYY')} &rarr; {moment(item.items[0].ngayKetThuc).format('DD/MM/YYYY')}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark" style={{ fontSize: '12px' }}>
                                                                    <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                    {item.dates[0]}
                                                                </Badge>
                                                            )
                                                        ) : (
                                                            <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                                                                {item.dates.map((d, dIdx) => (
                                                                    <span key={dIdx} className="badge badge-light border text-primary" style={{ fontSize: '11px', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }}>
                                                                        <i className="far fa-calendar-check mr-1 text-success"></i>{d}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {gvName !== '-' ? (
                                                            <span className="text-dark">
                                                                <i className="fas fa-chalkboard-teacher text-info mr-1"></i>
                                                                {gvName}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted small">Chưa phân công</span>
                                                        )}
                                                    </td>
                                                    <td>{item.trinhDo || '-'}</td>
                                                    <td className="text-muted small" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {item.ghiChu || '-'}
                                                    </td>
                                                    <td className="text-center">
                                                        {item.trangThai === 1 ? (
                                                            <Badge variant="success">Đã xác nhận</Badge>
                                                        ) : item.trangThai === 2 ? (
                                                            <Badge variant="warning">Cảnh báo</Badge>
                                                        ) : (
                                                            <Badge variant="secondary">Nháp</Badge>
                                                        )}
                                                    </td>
                                                    {isAdmin && (
                                                        <td className="text-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="p-1 mr-1"
                                                                title="Sửa lịch"
                                                                onClick={() => handleOpenEdit(item.representative)}
                                                            >
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="p-1"
                                                                title="Xóa lịch"
                                                                onClick={() => handleDeleteClick(item.representative)}
                                                            >
                                                                <i className="fas fa-trash-alt"></i>
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* VIEW 2: MATRIX VIEW */}
            {viewMode === 'matrix' && (
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white py-2">
                        <Row className="align-items-center justify-content-between">
                            <Col md={7} className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 mr-2 font-weight-bold small">Tháng:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        as="select"
                                        style={{ width: 105 }}
                                        value={thang}
                                        onChange={(e) => setThang(Number(e.target.value))}
                                        className="mr-2"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>Tháng {m}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Label className="mb-0 mr-2 font-weight-bold small">Năm:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        style={{ width: 85 }}
                                        value={nam}
                                        onChange={(e) => setNam(Number(e.target.value))}
                                    />
                                </div>

                                {isAdmin && (
                                    <div className="btn-group btn-group-sm ml-md-2" role="group">
                                        <button
                                            type="button"
                                            className={`btn ${matrixTarget === 'student' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => setMatrixTarget('student')}
                                            style={{ fontWeight: 500 }}
                                        >
                                            <i className="fas fa-user-graduate mr-1"></i> Theo Học Viên
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn ${matrixTarget === 'instructor' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => setMatrixTarget('instructor')}
                                            style={{ fontWeight: 500 }}
                                        >
                                            <i className="fas fa-chalkboard-teacher mr-1"></i> Theo Giảng Viên
                                        </button>
                                    </div>
                                )}

                                <div className="d-flex align-items-center ml-md-2 position-relative" style={{ width: 170 }}>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder={isAdmin && matrixTarget === 'instructor' ? "Tìm giảng viên..." : "Tìm học viên..."}
                                        value={isAdmin && matrixTarget === 'instructor' ? tutorFilter : studentFilter}
                                        onChange={(e) => {
                                            if (isAdmin && matrixTarget === 'instructor') {
                                                setTutorFilter(e.target.value);
                                            } else {
                                                setStudentFilter(e.target.value);
                                            }
                                        }}
                                        style={{ borderRadius: '6px', fontSize: '12px', paddingRight: '22px' }}
                                    />
                                    {(isAdmin && matrixTarget === 'instructor' ? tutorFilter : studentFilter) && (
                                        <span
                                            style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#999', fontSize: '14px', lineHeight: 1 }}
                                            onClick={() => {
                                                if (isAdmin && matrixTarget === 'instructor') setTutorFilter('');
                                                else setStudentFilter('');
                                            }}
                                        >
                                            &times;
                                        </span>
                                    )}
                                </div>
                            </Col>
                            <Col md={5} className="text-right small text-muted mt-2 mt-md-0">
                                <span className="mr-3">
                                    <span className="badge badge-success mr-1">&nbsp;</span> {matrixTarget === 'instructor' && isAdmin ? "Có lịch hướng dẫn" : "Có lịch học"}
                                </span>
                                <span>
                                    <span className="badge badge-danger mr-1">&nbsp;</span> Nghỉ
                                </span>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 360px)' }}>
                            <Table bordered hover size="sm" className="mb-0 text-center text-nowrap">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={{ position: 'sticky', left: 0, zIndex: 10, background: '#f8f9fa', width: 45 }}>#</th>
                                        <th style={{ position: 'sticky', left: 45, zIndex: 10, background: '#f8f9fa', minWidth: 200 }} className="text-left">
                                            {isAdmin && matrixTarget === 'instructor' ? 'Họ và tên giảng viên' : 'Họ và tên học viên'}
                                        </th>
                                        {matrixDates.map((dStr, idx) => {
                                            const mDate = moment(dStr);
                                            const isWeekend = mDate.day() === 0 || mDate.day() === 6;
                                            return (
                                                <th key={idx} style={{ minWidth: 55, backgroundColor: isWeekend ? '#fff3cd' : undefined }}>
                                                    <small className="font-weight-bold">{mDate.format('DD')}</small>
                                                    <br />
                                                    <span style={{ fontSize: '10px', color: '#6c757d' }}>{mDate.format('dd')}</span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {isMatrixLoading ? (
                                        <tr>
                                            <td colSpan={matrixDates.length + 2} className="py-4">
                                                <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                                                Đang tải ma trận lịch học...
                                            </td>
                                        </tr>
                                    ) : matrixRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={matrixDates.length + 2} className="py-4 text-muted">
                                                Chưa có dữ liệu lịch {isAdmin && matrixTarget === 'instructor' ? 'giảng dạy' : 'học'} cho tháng {thang}/{nam}.
                                            </td>
                                        </tr>
                                    ) : (
                                        matrixRows.map((row, rIdx) => {
                                            const isRowInstructor = isAdmin && matrixTarget === 'instructor';
                                            const rowTitle = row.hocVienTen || row.tutorTen || 'Chưa rõ';

                                            return (
                                                <tr key={rIdx}>
                                                    <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 5 }}>{rIdx + 1}</td>
                                                    <td style={{ position: 'sticky', left: 45, background: '#fff', zIndex: 5 }} className="text-left font-weight-bold">
                                                        <i className={`${isRowInstructor ? 'fas fa-chalkboard-teacher text-info' : 'fas fa-user-graduate text-muted'} mr-2`}></i>
                                                        {rowTitle}
                                                    </td>
                                                    {matrixDates.map((dStr, dIdx) => {
                                                        const cell = row.scheduleByDate ? row.scheduleByDate[dStr] : null;
                                                        let isNghi = false;
                                                        let isHoc = false;
                                                        let maKhoa = '';
                                                        let fullDonVi = '';
                                                        let gvTen = '';
                                                        let hocVienNames = '';
                                                        let trinhDo = '';
                                                        let ghiChu = '';

                                                        if (cell) {
                                                            if (typeof cell === 'string') {
                                                                if (cell === 'Nghỉ') isNghi = true;
                                                                else if (cell !== '-') {
                                                                    isHoc = true;
                                                                    maKhoa = cell;
                                                                    fullDonVi = cell;
                                                                }
                                                            } else {
                                                                if (cell.trangThai === 'Nghi') isNghi = true;
                                                                else if (cell.trangThai === 'Hoc' || cell.maDonVi || cell.donVi || cell.giangVienTen || cell.hocVienTen) {
                                                                    isHoc = true;
                                                                    maKhoa = cell.maDonVi || cell.donVi || '';
                                                                    fullDonVi = cell.donVi || cell.maDonVi || '';
                                                                    gvTen = cell.giangVienTen || '';
                                                                    hocVienNames = cell.hocVienTen || '';
                                                                    trinhDo = cell.trinhDo || '';
                                                                    ghiChu = cell.ghiChu || '';
                                                                }
                                                            }
                                                        }

                                                        let cellBg = "#fff";
                                                        if (isNghi) cellBg = "#f8d7da";
                                                        else if (isHoc) cellBg = isRowInstructor ? "#e3f2fd" : "#e8f5e9";

                                                        if (isNghi) {
                                                            return (
                                                                <td key={dIdx} style={{ backgroundColor: cellBg, padding: '6px 2px', verticalAlign: 'middle' }}>
                                                                    <span className="text-danger font-weight-bold" style={{ fontSize: '11px' }}>Nghỉ</span>
                                                                </td>
                                                            );
                                                        }

                                                        if (isHoc) {
                                                            const popover = (
                                                                <Popover id={`pop-${rIdx}-${dIdx}`} style={{ maxWidth: '330px', zIndex: 1060, boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}>
                                                                    <Popover.Title as="h6" className="font-weight-bold bg-light py-1 px-2 border-bottom mb-0 text-dark d-flex justify-content-between align-items-center">
                                                                        <span>
                                                                            <i className={`${isRowInstructor ? 'fas fa-chalkboard-teacher text-info' : 'far fa-calendar-alt text-primary'} mr-1`}></i> 
                                                                            {rowTitle}
                                                                        </span>
                                                                        <small className="text-muted">{moment(dStr).format('DD/MM/YYYY')}</small>
                                                                    </Popover.Title>
                                                                    <Popover.Content className="p-2 text-left small" style={{ lineHeight: 1.6, color: '#212529' }}>
                                                                        <div className="mb-1">
                                                                            🏥 <strong>Khoa / Đơn vị:</strong> {fullDonVi || maKhoa || 'Chưa rõ'}
                                                                        </div>
                                                                        {isRowInstructor ? (
                                                                            <div className="mb-1">
                                                                                🎓 <strong>Học viên hướng dẫn:</strong> {hocVienNames || 'Chưa rõ học viên'}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="mb-1">
                                                                                👨‍🏫 <strong>Giảng viên:</strong> {gvTen || 'Chưa phân công'}
                                                                            </div>
                                                                        )}
                                                                        {trinhDo && (
                                                                            <div className="mb-1">
                                                                                📝 <strong>Nội dung / Giai đoạn:</strong> {trinhDo}
                                                                            </div>
                                                                        )}
                                                                        {ghiChu && (
                                                                            <div className="text-muted font-italic">
                                                                                📋 <strong>Ghi chú:</strong> {ghiChu}
                                                                            </div>
                                                                        )}
                                                                    </Popover.Content>
                                                                </Popover>
                                                            );

                                                            return (
                                                                <OverlayTrigger key={dIdx} trigger={['hover', 'focus']} placement="auto" overlay={popover}>
                                                                    <td
                                                                        style={{ backgroundColor: cellBg, padding: '6px 2px', verticalAlign: 'middle', cursor: 'pointer' }}
                                                                    >
                                                                        <span className={`font-weight-bold ${isRowInstructor ? 'text-info' : 'text-primary'}`} style={{ fontSize: '11px' }}>
                                                                            {maKhoa || <i className="fas fa-check text-success font-weight-bold"></i>}
                                                                        </span>
                                                                    </td>
                                                                </OverlayTrigger>
                                                            );
                                                        }

                                                        return (
                                                            <td
                                                                key={dIdx}
                                                                style={{ backgroundColor: cellBg, padding: '6px 2px', verticalAlign: 'middle' }}
                                                            >
                                                                <span className="text-muted" style={{ opacity: 0.35 }}>-</span>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* VIEW 3: STUDENT TIMELINE VIEW */}
            {viewMode === 'student' && (
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white py-2">
                        <Row className="align-items-center justify-content-between">
                            <Col md={7} className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                {isStudent ? (
                                    <div className="d-flex align-items-center mr-3">
                                        <Badge variant="primary" className="p-2 mr-2" style={{ fontSize: '13px' }}>
                                            <i className="fas fa-user-graduate mr-1"></i> Học viên
                                        </Badge>
                                        <span className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
                                            {currentStudent?.hoTen || currentUser?.name || 'Học viên'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center mr-3" style={{ minWidth: 260 }}>
                                        <span className="font-weight-bold text-dark mr-2 text-nowrap small">Học Viên:</span>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <Select
                                                size="sm"
                                                options={studentOptions}
                                                value={studentOptions.find(opt => opt.value === selectedStudentId) || null}
                                                onChange={(opt) => setSelectedStudentId(opt ? opt.value : '')}
                                                placeholder="Tìm & chọn học viên..."
                                                isClearable={false}
                                                classNamePrefix="select-sm"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '13px',
                                                        borderRadius: '4px'
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0',
                                                        padding: '0'
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '32px'
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Tháng:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        as="select"
                                        style={{ width: 100 }}
                                        value={thang}
                                        onChange={(e) => setThang(Number(e.target.value))}
                                        className="mr-2"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>Tháng {m}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Năm:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        style={{ width: 85 }}
                                        value={nam}
                                        onChange={(e) => setNam(Number(e.target.value))}
                                    />
                                </div>
                            </Col>
                            <Col md={5} className="text-md-right text-muted small mt-2 mt-md-0">
                                Tổng cộng: <strong>{groupedStudentSchedules.length}</strong> giai đoạn đào tạo
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        {groupedStudentSchedules.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fas fa-user-clock fa-3x mb-3 text-secondary" style={{ opacity: 0.4 }}></i>
                                <p>Học viên này chưa có giai đoạn đào tạo nào được phân lịch.</p>
                                {isAdmin && (
                                    <Button variant="outline-primary" size="sm" onClick={() => setShowCreateModal(true)}>
                                        <i className="fas fa-plus mr-1"></i> Phân lịch cho học viên này
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="timeline-container px-3">
                                {groupedStudentSchedules.map((sch, sIdx) => {
                                    const gvName = sch.giangVienTen || (sch.giangViens && sch.giangViens[0]?.giangVienTen) || 'Chưa phân công';

                                    return (
                                        <div key={sch.key || sIdx} className="d-flex mb-3 pb-3 border-bottom">
                                            <div className="mr-3 text-center" style={{ minWidth: 60 }}>
                                                <Badge variant="primary" className="p-2" style={{ borderRadius: '50%', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {sIdx + 1}
                                                </Badge>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="font-weight-bold text-primary" style={{ fontSize: '15px' }}>
                                                        <i className="fas fa-hospital mr-1"></i> {sch.donVi || 'Chưa chọn khoa'}
                                                    </span>
                                                    {sch.trangThai === 1 ? (
                                                        <Badge variant="success">Đã xác nhận</Badge>
                                                    ) : sch.trangThai === 2 ? (
                                                        <Badge variant="warning">Cảnh báo</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Nháp</Badge>
                                                    )}
                                                </div>
                                                <div className="text-muted small mb-2">
                                                    {sch.items.length === 1 ? (
                                                        sch.isSingleRange ? (
                                                            <span className="font-weight-500">
                                                                <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                {moment(sch.items[0].ngayBatDau).format('DD/MM/YYYY')} &rarr; {moment(sch.items[0].ngayKetThuc).format('DD/MM/YYYY')}
                                                            </span>
                                                        ) : (
                                                            <span className="font-weight-500">
                                                                <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                {sch.dates[0]}
                                                            </span>
                                                        )
                                                    ) : (
                                                        <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                                                            {sch.dates.map((d, dIdx) => (
                                                                <span key={dIdx} className="badge badge-light border text-primary" style={{ fontSize: '11px', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }}>
                                                                    <i className="far fa-calendar-check mr-1 text-success"></i>
                                                                    {d}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-dark small mb-1">
                                                    <i className="fas fa-chalkboard-teacher mr-1 text-success"></i>
                                                    Giảng viên phụ trách: <strong>{gvName}</strong>
                                                </div>
                                                {sch.trinhDo && (
                                                    <div className="text-dark small mb-1">
                                                        <i className="fas fa-tasks mr-1 text-warning"></i>
                                                        Nội dung/Trình độ: {sch.trinhDo}
                                                    </div>
                                                )}
                                                {sch.ghiChu && (
                                                    <div className="text-muted small italic">
                                                        <i className="far fa-sticky-note mr-1"></i>
                                                        Ghi chú: {sch.ghiChu}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* VIEW 4: INSTRUCTOR AGENDA VIEW */}
            {viewMode === 'instructor' && (
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white py-2">
                        <Row className="align-items-center justify-content-between">
                            <Col md={7} className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                {isInstructor ? (
                                    <div className="d-flex align-items-center mr-3">
                                        <Badge variant="success" className="p-2 mr-2" style={{ fontSize: '13px' }}>
                                            <i className="fas fa-chalkboard-teacher mr-1"></i> Giảng viên
                                        </Badge>
                                        <span className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
                                            {currentTutor?.fullName || currentUser?.name || 'Giảng viên'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center mr-3" style={{ minWidth: 260 }}>
                                        <span className="font-weight-bold text-dark mr-2 text-nowrap small">Giảng Viên:</span>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <Select
                                                size="sm"
                                                options={instructorOptions}
                                                value={instructorOptions.find(opt => opt.value === selectedInstructorId) || null}
                                                onChange={(opt) => setSelectedInstructorId(opt ? opt.value : '')}
                                                placeholder="Tìm & chọn giảng viên..."
                                                isClearable={false}
                                                classNamePrefix="select-sm"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '13px',
                                                        borderRadius: '4px'
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0',
                                                        padding: '0'
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '32px'
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Tháng:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        as="select"
                                        style={{ width: 100 }}
                                        value={thang}
                                        onChange={(e) => setThang(Number(e.target.value))}
                                        className="mr-2"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>Tháng {m}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Năm:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        style={{ width: 85 }}
                                        value={nam}
                                        onChange={(e) => setNam(Number(e.target.value))}
                                    />
                                </div>
                            </Col>
                            <Col md={5} className="text-md-right text-muted small mt-2 mt-md-0">
                                Tổng số lượt phân công: <strong>{groupedInstructorSchedules.length}</strong>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table bordered hover size="sm" className="mb-0 align-middle">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={{ width: 50 }} className="text-center">#</th>
                                        <th style={{ minWidth: 160 }}>Học viên hướng dẫn</th>
                                        <th style={{ minWidth: 220 }}>Khoa / Phòng</th>
                                        <th style={{ minWidth: 200 }}>Thời gian</th>
                                        <th style={{ minWidth: 130 }}>Trình độ / Giai đoạn</th>
                                        <th style={{ minWidth: 120 }}>Ghi chú</th>
                                        {isAdmin && <th className="text-center" style={{ width: 90 }}>Thao tác</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedInstructorSchedules.length === 0 ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 7 : 6} className="text-center py-5 text-muted">
                                                <i className="fas fa-calendar-check fa-3x mb-3 text-secondary" style={{ opacity: 0.4 }}></i>
                                                <p>Giảng viên này hiện chưa được phân công hướng dẫn buổi học nào.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        groupedInstructorSchedules.map((item, idx) => {
                                            return (
                                                <tr key={item.key || item.id || idx}>
                                                    <td className="text-center text-muted">{idx + 1}</td>
                                                    <td className="font-weight-bold text-dark">
                                                        <i className="fas fa-user-graduate text-primary mr-2"></i>
                                                        {item.hocVienTen || 'Chưa rõ tên'}
                                                    </td>
                                                    <td style={{ minWidth: 220, whiteSpace: 'normal' }}>{item.donVi || '-'}</td>
                                                    <td style={{ minWidth: 200, whiteSpace: 'normal' }}>
                                                        {item.items.length === 1 ? (
                                                            item.isSingleRange ? (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark">
                                                                    {moment(item.items[0].ngayBatDau).format('DD/MM/YYYY')} &rarr; {moment(item.items[0].ngayKetThuc).format('DD/MM/YYYY')}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark">
                                                                    {item.dates[0]}
                                                                </Badge>
                                                            )
                                                        ) : (
                                                            <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                                                                {item.dates.map((d, dIdx) => (
                                                                    <span key={dIdx} className="badge badge-light border text-primary" style={{ fontSize: '11px', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }}>
                                                                        <i className="far fa-calendar-check mr-1 text-success"></i>{d}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{item.trinhDo || '-'}</td>
                                                    <td className="text-muted small">{item.ghiChu || '-'}</td>
                                                    {isAdmin && (
                                                        <td className="text-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="p-1 mr-1"
                                                                title="Sửa"
                                                                onClick={() => handleOpenEdit(item.representative)}
                                                            >
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="p-1"
                                                                title="Xóa"
                                                                onClick={() => handleDeleteClick(item.representative)}
                                                            >
                                                                <i className="fas fa-trash-alt"></i>
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}
            </div>

            {/* ===================== MOBILE VIEW (< 768px) CHUẨN MOCKUP ===================== */}
            <div className="d-block d-md-none">
                {/* 0. Top Subject Switcher (Đối tượng quản lý) */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                        <i className={mobileSubMode === 'student' ? "fas fa-user-graduate text-primary mr-1" : "fas fa-chalkboard-teacher text-info mr-1"}></i>
                        {mobileSubMode === 'student' ? 'Lịch theo học viên' : 'Lịch theo giảng viên'}
                    </div>
                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            type="button"
                            className={`btn btn-sm py-1 px-2 font-weight-500 ${mobileSubMode === 'student' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ borderRadius: '6px 0 0 6px', fontSize: '12px' }}
                            onClick={() => setMobileSubMode('student')}
                        >
                            Học viên ({studentsData.length})
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm py-1 px-2 font-weight-500 ${mobileSubMode === 'instructor' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ borderRadius: '0 6px 6px 0', fontSize: '12px' }}
                            onClick={() => setMobileSubMode('instructor')}
                        >
                            Giảng viên ({tutorsData.length})
                        </button>
                    </div>
                </div>

                {/* 1. Segmented Control Switcher (Theo ngày vs Lộ trình) */}
                <div
                    className="d-flex mb-3 rounded-lg overflow-hidden border bg-white"
                    style={{ borderRadius: '10px', borderColor: '#cbd5e1' }}
                >
                    <button
                        type="button"
                        className="btn flex-fill py-2 font-weight-bold text-center"
                        style={{
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: mobileViewType === 'by_day' ? '#3b82f6' : 'transparent',
                            color: mobileViewType === 'by_day' ? '#fff' : '#2563eb',
                            transition: 'all 0.2s',
                            margin: '2px'
                        }}
                        onClick={() => setMobileViewType('by_day')}
                    >
                        Theo ngày
                    </button>
                    <button
                        type="button"
                        className="btn flex-fill py-2 font-weight-bold text-center"
                        style={{
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: mobileViewType === 'by_roadmap' ? '#3b82f6' : 'transparent',
                            color: mobileViewType === 'by_roadmap' ? '#fff' : '#2563eb',
                            transition: 'all 0.2s',
                            margin: '2px'
                        }}
                        onClick={() => setMobileViewType('by_roadmap')}
                    >
                        {mobileSubMode === 'student' ? 'Theo học viên' : 'Theo giảng viên'}
                    </button>
                </div>

                {/* ===================== CHẾ ĐỘ 1: THEO NGÀY (DAY SLICE MATRIX) ===================== */}
                {mobileViewType === 'by_day' && (
                    <div>
                        {/* A. Dải ngày tương tác (Day Strip) */}
                        <div className="bg-white rounded-lg p-2 mb-2 border shadow-sm" style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="font-weight-500 text-dark small">Ngày đang xem</span>
                                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                                        style={{ height: '26px', borderRadius: '4px', fontSize: '11px' }}
                                        onClick={() => setSelectedDate(prev => moment(prev, 'YYYY-MM-DD').subtract(7, 'days').format('YYYY-MM-DD'))}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary py-0 px-2 font-weight-500"
                                        style={{ height: '26px', borderRadius: '4px', fontSize: '12px' }}
                                        onClick={() => setSelectedDate(moment().format('YYYY-MM-DD'))}
                                    >
                                        Hôm nay
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                                        style={{ height: '26px', borderRadius: '4px', fontSize: '11px' }}
                                        onClick={() => setSelectedDate(prev => moment(prev, 'YYYY-MM-DD').add(7, 'days').format('YYYY-MM-DD'))}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>

                            {/* 7 ngày tuần */}
                            <div className="d-flex justify-content-between" style={{ gap: '4px' }}>
                                {dayStripDates.map((d, dIdx) => {
                                    const isSelected = d.isSame(moment(selectedDate, 'YYYY-MM-DD'), 'day');
                                    const isWeekend = d.day() === 0 || d.day() === 6;
                                    const vnDay = formatVnDay(d);

                                    return (
                                        <div
                                            key={dIdx}
                                            className="text-center py-2 flex-fill"
                                            style={{
                                                cursor: 'pointer',
                                                borderRadius: '8px',
                                                backgroundColor: isSelected ? '#2563eb' : isWeekend ? '#fef9c3' : '#ffffff',
                                                border: isSelected ? '1px solid #2563eb' : isWeekend ? '1px solid #fef08a' : '1px solid #e2e8f0',
                                                color: isSelected ? '#ffffff' : isWeekend ? '#854d0e' : '#334155',
                                                minWidth: 0,
                                                transition: 'all 0.15s'
                                            }}
                                            onClick={() => setSelectedDate(d.format('YYYY-MM-DD'))}
                                        >
                                            <div className="font-weight-bold" style={{ fontSize: '14px', lineHeight: '1.1' }}>
                                                {d.format('D')}
                                            </div>
                                            <div style={{ fontSize: '11px', opacity: isSelected ? 0.9 : 0.7 }}>
                                                {vnDay.short}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* B. 3 Thẻ KPI tổng hợp ngày */}
                        <div className="d-flex mb-3" style={{ gap: '8px' }}>
                            {mobileSubMode === 'student' ? (
                                <>
                                    <div className="flex-fill bg-white rounded-lg p-2 border text-center shadow-sm" style={{ borderRadius: '10px' }}>
                                        <div className="font-weight-bold text-primary" style={{ fontSize: '18px', lineHeight: '1.1' }}>
                                            {studentDayKpis.coLich}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '11px' }}>có lịch hôm nay</div>
                                    </div>
                                    <div className="flex-fill bg-white rounded-lg p-2 border text-center shadow-sm" style={{ borderRadius: '10px' }}>
                                        <div className="font-weight-bold text-primary" style={{ fontSize: '18px', lineHeight: '1.1' }}>
                                            {studentDayKpis.chuaXep}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '11px' }}>chưa xếp lịch</div>
                                    </div>
                                    <div className="flex-fill bg-white rounded-lg p-2 border text-center shadow-sm" style={{ borderRadius: '10px' }}>
                                        <div className="font-weight-bold text-muted" style={{ fontSize: '18px', lineHeight: '1.1' }}>
                                            {studentDayKpis.vangNghi}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '11px' }}>vắng / nghỉ</div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex-fill bg-white rounded-lg p-2 border text-center shadow-sm" style={{ borderRadius: '10px' }}>
                                        <div className="font-weight-bold text-primary" style={{ fontSize: '18px', lineHeight: '1.1' }}>
                                            {instructorDayKpis.coLich}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '11px' }}>giảng viên có lịch</div>
                                    </div>
                                    <div className="flex-fill bg-white rounded-lg p-2 border text-center shadow-sm" style={{ borderRadius: '10px' }}>
                                        <div className="font-weight-bold text-primary" style={{ fontSize: '18px', lineHeight: '1.1' }}>
                                            {instructorDayKpis.conTrong}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '11px' }}>còn trống</div>
                                    </div>
                                    <div className="flex-fill bg-white rounded-lg p-2 border text-center shadow-sm" style={{ borderRadius: '10px' }}>
                                        <div className={`font-weight-bold ${instructorDayKpis.xungDot > 0 ? 'text-danger' : 'text-primary'}`} style={{ fontSize: '18px', lineHeight: '1.1' }}>
                                            {instructorDayKpis.xungDot}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '11px' }}>xung đột</div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* C. Tiêu đề danh sách ngày */}
                        <div className="font-weight-500 text-dark small mb-2">
                            {formatVnDay(moment(selectedDate, 'YYYY-MM-DD')).full}, {moment(selectedDate, 'YYYY-MM-DD').format('DD/MM/YYYY')} · {mobileSubMode === 'student' ? 'danh sách học viên' : 'phân công giảng viên'}
                        </div>

                        {/* D. Container danh sách ca thực hành */}
                        <div className="bg-white rounded-lg p-3 mb-3 border shadow-sm" style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}>
                            <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom">
                                <span className="font-weight-bold text-dark small">
                                    {mobileSubMode === 'student' ? 'Ca thực hành hôm nay' : 'Danh sách giảng viên'}
                                </span>
                                <span className="text-muted small">
                                    {mobileSubMode === 'student' ? '09:00–17:00' : `${instructorDayKpis.coLich} lượt phân công`}
                                </span>
                            </div>

                            {/* Danh sách học viên trong ngày */}
                            {mobileSubMode === 'student' && (
                                studentDayKpis.items.length === 0 ? (
                                    <div className="text-center py-4 text-muted small">
                                        Không có học viên nào trong lớp.
                                    </div>
                                ) : (
                                    studentDayKpis.items.map((it, idx) => {
                                        const cell = it.cell;
                                        const isHoc = it.trangThai === 'Hoc';
                                        const isNghi = it.trangThai === 'Nghi';

                                        return (
                                            <div
                                                key={idx}
                                                className="d-flex align-items-center justify-content-between py-2 border-bottom"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    if (cell?.lichHocId) {
                                                        const matchSch = scheduleList.find(s => s.id === cell.lichHocId);
                                                        if (matchSch) handleOpenEdit(matchSch);
                                                    } else {
                                                        setQuickCreateState({ studentId: it.studentId, date: selectedDate });
                                                        setShowCreateModal(true);
                                                    }
                                                }}
                                            >
                                                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                                    <div
                                                        className="d-flex align-items-center justify-content-center mr-2 rounded-circle font-weight-bold flex-shrink-0"
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            backgroundColor: isHoc ? '#e0f2fe' : isNghi ? '#f1f5f9' : '#fee2e2',
                                                            color: isHoc ? '#0369a1' : isNghi ? '#64748b' : '#b91c1c',
                                                            fontSize: '11px'
                                                        }}
                                                    >
                                                        {getInitials(it.studentName)}
                                                    </div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="font-weight-bold text-dark text-truncate" style={{ fontSize: '13px' }}>
                                                            {it.studentName}
                                                        </div>
                                                        <div className="text-muted small text-truncate" style={{ fontSize: '11px' }}>
                                                            {isHoc
                                                                ? `${cell?.donVi || 'Lâm sàng'}${cell?.maDonVi ? ` · ${cell.maDonVi}` : ''}${cell?.giangVienTen ? ` · ${cell.giangVienTen}` : ''}`
                                                                : isNghi
                                                                ? 'Nghỉ theo lịch'
                                                                : 'Chưa có khoa thực hành'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="ml-2 flex-shrink-0">
                                                    {isHoc ? (
                                                        <span
                                                            className="badge px-2 py-1 font-weight-500"
                                                            style={{ backgroundColor: '#dcfce7', color: '#15803d', borderRadius: '4px', fontSize: '11px' }}
                                                        >
                                                            Đã xếp
                                                        </span>
                                                    ) : isNghi ? (
                                                        <span className="text-muted small font-weight-500" style={{ fontSize: '11px' }}>
                                                            Nghỉ
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="badge px-2 py-1 font-weight-500"
                                                            style={{ backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '4px', fontSize: '11px' }}
                                                        >
                                                            Chưa xếp
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )
                            )}

                            {/* Danh sách giảng viên trong ngày */}
                            {mobileSubMode === 'instructor' && (
                                instructorDayKpis.items.length === 0 ? (
                                    <div className="text-center py-4 text-muted small">
                                        Không có giảng viên nào trong lớp.
                                    </div>
                                ) : (
                                    instructorDayKpis.items.map((it, idx) => {
                                        const cell = it.cell;
                                        const isHoc = it.trangThai === 'Hoc';

                                        return (
                                            <div
                                                key={idx}
                                                className="d-flex align-items-center justify-content-between py-2 border-bottom"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    if (cell?.lichHocId) {
                                                        const matchSch = scheduleList.find(s => s.id === cell.lichHocId);
                                                        if (matchSch) handleOpenEdit(matchSch);
                                                    }
                                                }}
                                            >
                                                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                                    <div
                                                        className="d-flex align-items-center justify-content-center mr-2 rounded-circle font-weight-bold flex-shrink-0"
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            backgroundColor: isHoc ? '#e0f2fe' : '#f1f5f9',
                                                            color: isHoc ? '#0369a1' : '#64748b',
                                                            fontSize: '11px'
                                                        }}
                                                    >
                                                        {getInitials(it.tutorName)}
                                                    </div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="font-weight-bold text-dark text-truncate" style={{ fontSize: '13px' }}>
                                                            {it.tutorName}
                                                        </div>
                                                        <div className="text-muted small text-truncate" style={{ fontSize: '11px' }}>
                                                            {isHoc
                                                                ? `${cell?.maDonVi || cell?.donVi} · ${cell?.hocVienTen || 'Học viên'} · 09:00–17:00`
                                                                : 'Chưa có lịch trong ngày'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="ml-2 flex-shrink-0">
                                                    {isHoc ? (
                                                        <span
                                                            className="badge px-2 py-1 font-weight-500"
                                                            style={{ backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '4px', fontSize: '11px' }}
                                                        >
                                                            {cell?.maDonVi || 'Khoa'}
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="badge px-2 py-1 font-weight-500"
                                                            style={{ backgroundColor: '#dcfce7', color: '#15803d', borderRadius: '4px', fontSize: '11px' }}
                                                        >
                                                            Trống
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )
                            )}
                        </div>

                        {/* E. Chú thích cho Lịch học viên */}
                        {mobileSubMode === 'student' && (
                            <div className="mb-3 px-1">
                                <div className="d-flex align-items-center small text-muted mb-1" style={{ gap: '12px', fontSize: '11px' }}>
                                    <span><span style={{ color: '#16a34a' }}>●</span> Đã xếp</span>
                                    <span><span style={{ color: '#2563eb' }}>●</span> Đang chọn</span>
                                    <span><span style={{ color: '#ca8a04' }}>●</span> Cuối tuần</span>
                                </div>
                                <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                    Thao tác nhanh: chạm vào học viên để mở chi tiết hoặc đổi khoa, giảng viên và trạng thái.
                                </div>
                            </div>
                        )}

                        {/* F. Thanh đo Tải giảng viên trong tháng */}
                        {mobileSubMode === 'instructor' && (
                            <div className="bg-white rounded-lg p-3 mb-3 border shadow-sm" style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}>
                                <div className="font-weight-bold text-dark small mb-2">
                                    Tải giảng viên trong tháng
                                </div>
                                {tutorWorkloads.length === 0 ? (
                                    <div className="text-center py-3 text-muted small">Chưa có dữ liệu tải giảng viên.</div>
                                ) : (
                                    tutorWorkloads.slice(0, 5).map((tw, twIdx) => (
                                        <div key={twIdx} className="mb-2">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="small text-dark font-weight-500" style={{ fontSize: '12px' }}>
                                                    {tw.tutorName}
                                                </span>
                                                <span className="text-muted small" style={{ fontSize: '11px' }}>
                                                    {tw.activeDays}/{tw.totalDays}
                                                </span>
                                            </div>
                                            <div className="progress" style={{ height: '6px', borderRadius: '3px', backgroundColor: '#e2e8f0' }}>
                                                <div
                                                    className="progress-bar"
                                                    role="progressbar"
                                                    style={{ width: `${tw.pct}%`, backgroundColor: '#2563eb', borderRadius: '3px' }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* G. Cảnh báo trùng lịch */}
                        {mobileSubMode === 'instructor' && instructorDayKpis.xungDot > 0 && (
                            <div
                                className="p-3 mb-3 rounded-lg d-flex align-items-center"
                                style={{ backgroundColor: '#fefce8', border: '1px solid #fef08a', color: '#854d0e', borderRadius: '10px', fontSize: '12px' }}
                            >
                                <i className="fas fa-exclamation-triangle mr-2 text-warning" style={{ fontSize: '15px' }}></i>
                                <span>
                                    <strong>{instructorDayKpis.xungDot} giảng viên bị trùng lịch</strong> · Chạm để xem và đổi phân công
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* ===================== CHẾ ĐỘ 2: THEO HỌC VIÊN / THEO GIẢNG VIÊN (ROADMAPPING) ===================== */}
                {mobileViewType === 'by_roadmap' && (
                    <div>
                        {/* 1. MOBILE LỘ TRÌNH HỌC VIÊN */}
                        {mobileSubMode === 'student' && (
                            <div>
                                {/* Filter Row: Học viên + Tháng/Năm */}
                                <div className="row no-gutters mb-2" style={{ gap: '8px' }}>
                                    <div className="col" style={{ flex: '1 1 58%' }}>
                                        <div className="text-muted small mb-1 font-weight-500">Học viên</div>
                                        <Select
                                            size="sm"
                                            options={studentOptions}
                                            value={studentOptions.find(opt => opt.value === selectedStudentId) || null}
                                            onChange={(opt) => setSelectedStudentId(opt ? opt.value : '')}
                                            placeholder="Chọn học viên..."
                                            isClearable={false}
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '38px',
                                                    height: '38px',
                                                    borderRadius: '8px',
                                                    borderColor: '#cbd5e1',
                                                    fontSize: '13px',
                                                    backgroundColor: '#fff'
                                                }),
                                                valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                                indicatorsContainer: (base) => ({ ...base, height: '38px' })
                                            }}
                                        />
                                    </div>
                                    <div className="col" style={{ flex: '1 1 38%' }}>
                                        <div className="text-muted small mb-1 font-weight-500">Tháng / năm</div>
                                        <select
                                            className="form-control"
                                            style={{
                                                height: '38px',
                                                borderRadius: '8px',
                                                borderColor: '#cbd5e1',
                                                fontSize: '13px',
                                                backgroundColor: '#fff'
                                            }}
                                            value={`${thang}/${nam}`}
                                            onChange={(e) => {
                                                const [m, y] = e.target.value.split('/');
                                                setThang(Number(m));
                                                setNam(Number(y));
                                            }}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                                <option key={m} value={`${m}/${nam}`}>T{m} / {nam}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Counter */}
                                <div className="text-muted small mb-3">
                                    Tổng cộng: <strong>{groupedStudentSchedules.length}</strong> giai đoạn đào tạo
                                </div>

                                {/* List Phase Cards */}
                                {isStudentLoading ? (
                                    <div className="text-center py-4 text-muted bg-white rounded-lg border">
                                        <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                                        Đang tải dữ liệu...
                                    </div>
                                ) : groupedStudentSchedules.length === 0 ? (
                                    <div className="text-center py-5 bg-white rounded-lg border text-muted shadow-sm" style={{ borderRadius: '12px' }}>
                                        <i className="far fa-calendar-times fa-3x mb-2 text-secondary" style={{ opacity: 0.35 }}></i>
                                        <div className="font-weight-500 mb-1">Chưa có giai đoạn đào tạo nào trong tháng này.</div>
                                        {isAdmin && (
                                            <Button variant="outline-primary" size="sm" className="mt-2" onClick={() => setShowCreateModal(true)}>
                                                <i className="fas fa-plus mr-1"></i> Phân lịch học
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    groupedStudentSchedules.map((sch, sIdx) => {
                                        const gvName = sch.giangVienTen || (sch.giangViens && sch.giangViens.map(g => g.giangVienTen).filter(Boolean).join(', ')) || 'Chưa phân công';
                                        const dateDisplay = sch.items.length === 1 && sch.isSingleRange
                                            ? `${moment(sch.items[0].ngayBatDau).format('DD/MM/YYYY')} → ${moment(sch.items[0].ngayKetThuc).format('DD/MM/YYYY')}`
                                            : sch.dates.join(' · ');

                                        return (
                                            <div
                                                key={sch.key || sIdx}
                                                className="bg-white rounded-lg p-3 mb-3 border shadow-sm"
                                                style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}
                                            >
                                                {/* Card Header: Circle Number + Khoa */}
                                                <div className="d-flex align-items-center mb-2">
                                                    <div
                                                        className="mr-2 d-flex align-items-center justify-content-center text-white font-weight-bold flex-shrink-0"
                                                        style={{
                                                            width: '26px',
                                                            height: '26px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#3b82f6',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        {sIdx + 1}
                                                    </div>
                                                    <div className="font-weight-bold" style={{ color: '#2563eb', fontSize: '15px', lineHeight: '1.3' }}>
                                                        {sch.donVi || 'Chưa chọn khoa'}
                                                    </div>
                                                </div>

                                                {/* Date Line */}
                                                <div className="text-muted small mb-1 d-flex align-items-center" style={{ fontSize: '13px' }}>
                                                    <i className="far fa-calendar-alt mr-2 text-secondary" style={{ width: '16px' }}></i>
                                                    <span>{dateDisplay}</span>
                                                </div>

                                                {/* Instructor Line */}
                                                <div className="text-muted small mb-1 d-flex align-items-center" style={{ fontSize: '13px' }}>
                                                    <i className="far fa-user mr-2 text-secondary" style={{ width: '16px' }}></i>
                                                    <span>{gvName}</span>
                                                </div>

                                                {/* Content / Level Line */}
                                                <div className="text-muted small mb-2 d-flex align-items-center" style={{ fontSize: '13px' }}>
                                                    <i className="fas fa-bars mr-2 text-secondary" style={{ width: '16px' }}></i>
                                                    <span>Nội dung/Trình độ: {sch.trinhDo || 'abc'}</span>
                                                </div>

                                                {/* Status Badge */}
                                                <div>
                                                    <span
                                                        className="badge px-2 py-1 font-weight-500"
                                                        style={{
                                                            backgroundColor: sch.trangThai === 1 ? '#22c55e' : sch.trangThai === 2 ? '#f59e0b' : '#64748b',
                                                            color: '#fff',
                                                            borderRadius: '4px',
                                                            fontSize: '11px'
                                                        }}
                                                    >
                                                        {sch.trangThai === 1 ? 'Đã xác nhận' : sch.trangThai === 2 ? 'Cảnh báo' : 'Nháp'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {/* 2. MOBILE LỊCH GIẢNG VIÊN */}
                        {mobileSubMode === 'instructor' && (
                            <div>
                                {/* Filter Row: Giảng viên + Tháng/Năm */}
                                <div className="row no-gutters mb-2" style={{ gap: '8px' }}>
                                    <div className="col" style={{ flex: '1 1 58%' }}>
                                        <div className="text-muted small mb-1 font-weight-500">Giảng viên</div>
                                        <Select
                                            size="sm"
                                            options={instructorOptions}
                                            value={instructorOptions.find(opt => opt.value === selectedInstructorId) || null}
                                            onChange={(opt) => setSelectedInstructorId(opt ? opt.value : '')}
                                            placeholder="Chọn giảng viên..."
                                            isClearable={false}
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '38px',
                                                    height: '38px',
                                                    borderRadius: '8px',
                                                    borderColor: '#cbd5e1',
                                                    fontSize: '13px',
                                                    backgroundColor: '#fff'
                                                }),
                                                valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                                indicatorsContainer: (base) => ({ ...base, height: '38px' })
                                            }}
                                        />
                                    </div>
                                    <div className="col" style={{ flex: '1 1 38%' }}>
                                        <div className="text-muted small mb-1 font-weight-500">Tháng / năm</div>
                                        <select
                                            className="form-control"
                                            style={{
                                                height: '38px',
                                                borderRadius: '8px',
                                                borderColor: '#cbd5e1',
                                                fontSize: '13px',
                                                backgroundColor: '#fff'
                                            }}
                                            value={`${thang}/${nam}`}
                                            onChange={(e) => {
                                                const [m, y] = e.target.value.split('/');
                                                setThang(Number(m));
                                                setNam(Number(y));
                                            }}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                                <option key={m} value={`${m}/${nam}`}>T{m} / {nam}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Counter */}
                                <div className="text-muted small mb-3">
                                    Tổng số lượt phân công: <strong>{groupedInstructorSchedules.length}</strong>
                                </div>

                                {/* List Assignment Cards */}
                                {isInstructorLoading ? (
                                    <div className="text-center py-4 text-muted bg-white rounded-lg border">
                                        <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                                        Đang tải dữ liệu...
                                    </div>
                                ) : groupedInstructorSchedules.length === 0 ? (
                                    <div className="text-center py-5 bg-white rounded-lg border text-muted shadow-sm" style={{ borderRadius: '12px' }}>
                                        <i className="far fa-calendar-check fa-3x mb-2 text-secondary" style={{ opacity: 0.35 }}></i>
                                        <div className="font-weight-500">Giảng viên này chưa có lịch phân công trong tháng này.</div>
                                    </div>
                                ) : (
                                    groupedInstructorSchedules.map((item, idx) => {
                                        const dateDisplay = item.items.length === 1 && item.isSingleRange
                                            ? `${moment(item.items[0].ngayBatDau).format('DD/MM/YYYY')} → ${moment(item.items[0].ngayKetThuc).format('DD/MM/YYYY')}`
                                            : item.dates.join(' · ');

                                        return (
                                            <div
                                                key={item.key || idx}
                                                className="bg-white rounded-lg p-3 mb-3 border shadow-sm"
                                                style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}
                                            >
                                                {/* Card Header: Student Name */}
                                                <div className="d-flex align-items-center mb-2 font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                                                    <i className="far fa-user mr-2 text-dark"></i>
                                                    <span>{item.hocVienTen || 'Học viên'}</span>
                                                </div>

                                                {/* Body Lines */}
                                                <div className="text-muted small mb-1 pl-4" style={{ fontSize: '13px' }}>
                                                    {item.donVi || 'Chưa chọn khoa'}
                                                </div>
                                                <div className="text-muted small mb-1 pl-4" style={{ fontSize: '13px' }}>
                                                    Thời gian: {dateDisplay}
                                                </div>
                                                <div className="text-muted small mb-3 pl-4" style={{ fontSize: '13px' }}>
                                                    Trình độ: {item.trinhDo || 'abc'} {item.ghiChu ? ` · Ghi chú: ${item.ghiChu}` : ''}
                                                </div>

                                                {/* Action Buttons Row */}
                                                <div className="d-flex" style={{ gap: '8px' }}>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="flex-fill d-flex align-items-center justify-content-center py-2"
                                                        style={{ borderRadius: '8px', fontSize: '13px', fontWeight: '500' }}
                                                        onClick={() => handleOpenEdit(item.representative)}
                                                    >
                                                        <i className="fas fa-pencil-alt mr-1"></i> Chỉnh sửa
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="flex-fill d-flex align-items-center justify-content-center py-2"
                                                        style={{ borderRadius: '8px', fontSize: '13px', fontWeight: '500' }}
                                                        onClick={() => handleOpenDetail(item)}
                                                    >
                                                        <i className="fas fa-bars mr-1"></i> Chi tiết
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* MODAL: DETAIL */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="sm">
                <Modal.Header closeButton className="py-2 bg-light">
                    <Modal.Title className="h6 font-weight-bold mb-0">
                        <i className="fas fa-info-circle text-primary mr-1"></i> Chi tiết phân công
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    {detailItem && (
                        <div>
                            <div className="mb-2">
                                <span className="text-muted small d-block">Học viên:</span>
                                <strong className="text-dark" style={{ fontSize: '14px' }}>{detailItem.hocVienTen}</strong>
                            </div>
                            <div className="mb-2">
                                <span className="text-muted small d-block">Khoa / Đơn vị:</span>
                                <strong className="text-primary">{detailItem.donVi || '—'}</strong>
                            </div>
                            <div className="mb-2">
                                <span className="text-muted small d-block">Thời gian:</span>
                                <span>
                                    {detailItem.items?.length === 1 && detailItem.isSingleRange
                                        ? `${moment(detailItem.items[0].ngayBatDau).format('DD/MM/YYYY')} → ${moment(detailItem.items[0].ngayKetThuc).format('DD/MM/YYYY')}`
                                        : detailItem.dates?.join(', ')}
                                </span>
                            </div>
                            <div className="mb-2">
                                <span className="text-muted small d-block">Giảng viên phụ trách:</span>
                                <span>{detailItem.giangVienTen || (detailItem.giangViens && detailItem.giangViens[0]?.giangVienTen) || 'Chưa phân công'}</span>
                            </div>
                            <div className="mb-2">
                                <span className="text-muted small d-block">Nội dung / Trình độ:</span>
                                <span>{detailItem.trinhDo || '—'}</span>
                            </div>
                            {detailItem.ghiChu && (
                                <div className="mb-2">
                                    <span className="text-muted small d-block">Ghi chú:</span>
                                    <span className="font-italic">{detailItem.ghiChu}</span>
                                </div>
                            )}
                            <div>
                                <span className="text-muted small d-block mb-1">Trạng thái:</span>
                                <Badge variant={detailItem.trangThai === 1 ? 'success' : detailItem.trangThai === 2 ? 'warning' : 'secondary'}>
                                    {detailItem.trangThai === 1 ? 'Đã xác nhận' : detailItem.trangThai === 2 ? 'Cảnh báo' : 'Nháp'}
                                </Badge>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
                        Đóng
                    </Button>
                    {isAdmin && detailItem && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                                setShowDetailModal(false);
                                handleOpenEdit(detailItem.representative);
                            }}
                        >
                            <i className="fas fa-edit mr-1"></i> Chỉnh sửa
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* MODAL: CREATE SCHEDULE */}
            <CreateScheduleLienTucModal
                show={showCreateModal}
                onHide={() => {
                    setShowCreateModal(false);
                    setQuickCreateState({ studentId: null, date: null });
                }}
                lopId={lopId}
                lopDetail={lopDetail}
                studentsData={studentsData}
                initialStudentId={quickCreateState.studentId}
                initialDate={quickCreateState.date}
                onSuccess={() => {
                    refetchSchedule();
                    refetchAdminMatrix();
                    refetchStudentMatrix();
                    refetchInstructorMatrix();
                    queryClient.invalidateQueries(['giangViensLienTuc', lopId]);
                    queryClient.invalidateQueries(['lopDaoTaoLienTucDetail', lopId]);
                }}
            />

            {/* MODAL: EDIT SCHEDULE */}
            <EditScheduleLienTucModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                }}
                item={editingItem}
                lopDetail={lopDetail}
                onSuccess={() => {
                    refetchSchedule();
                    refetchAdminMatrix();
                    refetchStudentMatrix();
                    refetchInstructorMatrix();
                    queryClient.invalidateQueries(['giangViensLienTuc', lopId]);
                    queryClient.invalidateQueries(['lopDaoTaoLienTucDetail', lopId]);
                }}
            />



            {/* MODAL: CONFIRM DELETE */}
            <ConfirmModal
                isOpen={showDeleteModal}
                confirmType="delete"
                title="Xác nhận xóa lịch học"
                message={`Bạn có chắc chắn muốn xóa dòng lịch học của học viên "${itemToDelete?.hocVienTen}" tại đơn vị "${itemToDelete?.donVi || 'Chưa rõ'}"?`}
                handleAccept={handleConfirmDelete}
                handleCancel={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                }}
            />
        </div>
    );
};

export default ScheduleLienTucTab;
